import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  Building2,
  CalendarDays,
  ChevronDown,
  ChevronUp,
  CircleDollarSign,
  Eye,
  EyeOff,
  Info,
  Layers3,
  MapPinned,
  Megaphone,
  MousePointer2,
  PieChart,
  ReceiptText,
  RefreshCw,
  RotateCcw,
  Sparkles,
  Tags,
  Users,
  WalletCards,
} from 'lucide-react';
import { API_URL } from '../../config';
import { useInteractionPreferences } from '../../contexts/InteractionPreferencesContext';
import { haptic } from '../../utils/vibration';
import useToast from '../../hooks/useToast';
import FloatingField from '../../components/FloatingField';

const CHART_COLORS = ['#10b981', '#0ea5e9', '#6366f1', '#a855f7', '#f59e0b', '#14b8a6', '#84cc16', '#ec4899'];
const NEGATIVE_COLORS = ['#f43f5e', '#ef4444', '#fb7185', '#e11d48', '#f97316', '#dc2626'];

const CHART_METRICS = [
  { key: 'profit', label: 'Прибыль', centerLabel: 'результат' },
  { key: 'revenue', label: 'Выручка', centerLabel: 'выручка' },
  { key: 'expenses', label: 'Расходы', centerLabel: 'расходы' },
];

const DIMENSIONS = [
  {
    key: 'orders',
    title: 'Заказы',
    description: 'Какие заказы создают или съедают прибыль',
    icon: ReceiptText,
  },
  {
    key: 'clients',
    title: 'Клиенты',
    description: 'Финансовый результат по клиентской базе',
    icon: Users,
  },
  {
    key: 'directions',
    title: 'Направления',
    description: 'Стирка, выездная химчистка и дополнительные заказы',
    icon: Layers3,
  },
  {
    key: 'departments',
    title: 'Подразделения',
    description: 'Сравнение финансового результата подразделений',
    icon: Building2,
  },
  {
    key: 'services',
    title: 'Услуги',
    description: 'Прибыль распределена по составу заказов',
    icon: Tags,
  },
  {
    key: 'routes',
    title: 'Маршруты забора',
    description: 'Группировка по дате забора и подразделению',
    icon: MapPinned,
  },
  {
    key: 'sources',
    title: 'Источники клиентов',
    description: 'Какие каналы приводят прибыльные заказы',
    icon: Megaphone,
  },
];

const formatDateValue = (value) => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const currentMonthRange = () => {
  const today = new Date();
  return {
    from: formatDateValue(new Date(today.getFullYear(), today.getMonth(), 1)),
    to: formatDateValue(today),
  };
};

const rollingRange = (days) => {
  const today = new Date();
  const from = new Date(today);
  from.setDate(today.getDate() - Math.max(0, days - 1));
  return { from: formatDateValue(from), to: formatDateValue(today) };
};

const currentYearRange = () => {
  const today = new Date();
  return {
    from: formatDateValue(new Date(today.getFullYear(), 0, 1)),
    to: formatDateValue(today),
  };
};

const formatCurrency = (value) =>
  `${Number(value || 0).toLocaleString('ru-RU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })} ₽`;

const formatCompactCurrency = (value) => {
  const amount = Number(value || 0);
  const abs = Math.abs(amount);
  const sign = amount < 0 ? '−' : '';
  if (abs >= 1_000_000) {
    return `${sign}${(abs / 1_000_000).toLocaleString('ru-RU', {
      maximumFractionDigits: abs >= 10_000_000 ? 0 : 1,
    })} млн ₽`;
  }
  if (abs >= 10_000) {
    return `${sign}${(abs / 1_000).toLocaleString('ru-RU', {
      maximumFractionDigits: abs >= 100_000 ? 0 : 1,
    })} тыс ₽`;
  }
  return formatCurrency(amount).replace('-', '−');
};

const formatPercent = (value) => {
  if (value === null || value === undefined) return '—';
  return `${Number(value || 0).toLocaleString('ru-RU', { maximumFractionDigits: 1 })}%`;
};

const getMetricValue = (row, metricKey) => Number(row?.[metricKey] || 0);

const hashString = (value) => {
  let hash = 0;
  const source = String(value || '');
  for (let index = 0; index < source.length; index += 1) {
    hash = ((hash << 5) - hash) + source.charCodeAt(index);
    hash |= 0;
  }
  return Math.abs(hash);
};

const aggregateRows = (rows, label = 'Прочее', key = '__other__') => {
  const aggregated = rows.reduce((result, row) => ({
    revenue: result.revenue + Number(row.revenue || 0),
    direct_expenses: result.direct_expenses + Number(row.direct_expenses || 0),
    allocated_expenses: result.allocated_expenses + Number(row.allocated_expenses || 0),
    expenses: result.expenses + Number(row.expenses || 0),
    profit: result.profit + Number(row.profit || 0),
    orders_count: result.orders_count + Number(row.orders_count || 0),
  }), {
    revenue: 0,
    direct_expenses: 0,
    allocated_expenses: 0,
    expenses: 0,
    profit: 0,
    orders_count: 0,
  });
  return {
    key,
    label,
    ...aggregated,
    margin: aggregated.revenue > 0 ? (aggregated.profit / aggregated.revenue) * 100 : null,
    memberKeys: rows.map((row) => String(row.key)),
  };
};

const sortRowsByMetric = (rows, metricKey) => {
  return [...(rows || [])]
    .filter((row) => Math.abs(getMetricValue(row, metricKey)) >= 0.005)
    .sort((left, right) => (
      Math.abs(getMetricValue(right, metricKey)) - Math.abs(getMetricValue(left, metricKey))
    ));
};

const buildChartGroups = (rows, metricKey, limit = 8) => {
  const sorted = sortRowsByMetric(rows, metricKey);
  if (sorted.length <= limit) {
    return sorted.map((row) => ({
      ...row,
      memberKeys: [String(row.key)],
    }));
  }
  if (metricKey === 'profit') {
    const directRows = sorted.slice(0, limit - 2);
    const remainingRows = sorted.slice(limit - 2);
    const remainingProfit = remainingRows.filter((row) => getMetricValue(row, metricKey) >= 0);
    const remainingLoss = remainingRows.filter((row) => getMetricValue(row, metricKey) < 0);
    return [
      ...directRows.map((row) => ({
        ...row,
        memberKeys: [String(row.key)],
      })),
      ...(remainingProfit.length > 0
        ? [aggregateRows(remainingProfit, 'Остальная прибыль', '__other_profit__')]
        : []),
      ...(remainingLoss.length > 0
        ? [aggregateRows(remainingLoss, 'Остальные убытки', '__other_loss__')]
        : []),
    ];
  }
  return [
    ...sorted.slice(0, limit - 1).map((row) => ({
      ...row,
      memberKeys: [String(row.key)],
    })),
    aggregateRows(sorted.slice(limit - 1), 'Прочее', '__other__'),
  ];
};

const addChartColors = (rows, metricKey) => {
  return rows.map((row) => {
    const colorValue = row.colorMetricValue ?? getMetricValue(row, metricKey);
    const isLoss = metricKey === 'profit' && colorValue < 0;
    const palette = isLoss ? NEGATIVE_COLORS : CHART_COLORS;
    let color = palette[hashString(row.key) % palette.length];
    if (row.key === '__other__') color = '#94a3b8';
    if (row.key === '__other_profit__') color = '#34d399';
    if (row.key === '__other_loss__') color = '#fb7185';
    return {
      ...row,
      chartValue: getMetricValue(row, metricKey),
      color,
      isLoss,
    };
  });
};

const AnimatedDonutCanvas = ({
  rows,
  metricKey,
  hoveredKey,
  onHover,
  onToggle,
  enabledCount,
  totalCount,
}) => {
  const { animationsEnabled } = useInteractionPreferences();
  const [drawn, setDrawn] = useState(() => !animationsEnabled);

  useEffect(() => {
    if (!animationsEnabled) {
      setDrawn(true);
      return undefined;
    }
    if (drawn) return undefined;
    let secondFrame;
    const firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => setDrawn(true));
    });
    return () => {
      window.cancelAnimationFrame(firstFrame);
      if (secondFrame) window.cancelAnimationFrame(secondFrame);
    };
  }, [animationsEnabled, drawn]);

  const radius = 112;
  const circumference = 2 * Math.PI * radius;
  const activeRows = rows.filter((row) => Math.abs(row.chartValue) >= 0.005);
  const totalMagnitude = activeRows.reduce((sum, row) => sum + Math.abs(row.chartValue), 0);
  const netValue = activeRows.reduce((sum, row) => sum + row.chartValue, 0);
  const hoveredRow = activeRows.find((row) => row.key === hoveredKey);
  const centerValue = hoveredRow ? hoveredRow.chartValue : netValue;
  const centerShare = hoveredRow && totalMagnitude > 0
    ? (Math.abs(hoveredRow.chartValue) / totalMagnitude) * 100
    : null;
  const metric = CHART_METRICS.find((item) => item.key === metricKey) || CHART_METRICS[0];
  let offset = 0;

  return (
    <div className="relative mx-auto aspect-square w-full max-w-[310px]">
      <div className="pointer-events-none absolute inset-[15%] rounded-full bg-emerald-400/10 blur-2xl motion-safe:animate-pulse dark:bg-emerald-400/5" />
      <svg
        viewBox="0 0 320 320"
        className="relative h-full w-full overflow-visible"
        role="img"
        aria-label={`${metric.label}: ${formatCurrency(netValue)}. Включено ${enabledCount} из ${totalCount} позиций.`}
      >
        <title>{`${metric.label} по выбранному разрезу`}</title>
        <desc>Нажатие на сектор временно исключает его из расчёта диаграммы.</desc>
        <circle
          cx="160"
          cy="160"
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth="42"
          className="text-gray-100 dark:text-gray-800"
        />
        {activeRows.map((row, index) => {
          const rawLength = totalMagnitude > 0
            ? (Math.abs(row.chartValue) / totalMagnitude) * circumference
            : 0;
          const gap = activeRows.length > 1 ? Math.min(5, rawLength * 0.18) : 0;
          const segmentLength = Math.max(0, rawLength - gap);
          const isHovered = hoveredKey === row.key;
          const isDimmed = hoveredKey && !isHovered;
          const segmentOffset = offset;
          offset += rawLength;

          return (
            <circle
              key={row.key}
              cx="160"
              cy="160"
              r={radius}
              fill="none"
              stroke={row.color}
              strokeWidth={isHovered ? 50 : 42}
              strokeLinecap="round"
              strokeDasharray={drawn
                ? `${segmentLength} ${Math.max(0, circumference - segmentLength)}`
                : `0 ${circumference}`}
              strokeDashoffset={drawn ? -segmentOffset : 0}
              transform="rotate(-90 160 160)"
              opacity={isDimmed ? 0.3 : 1}
              onMouseEnter={() => onHover(row.key)}
              onMouseLeave={() => onHover(null)}
              onClick={() => onToggle(row)}
              className="cursor-pointer"
              style={{
                filter: isHovered ? `drop-shadow(0 0 8px ${row.color})` : 'none',
                transitionProperty: 'stroke-dasharray, stroke-dashoffset, stroke-width, opacity, filter',
                transitionDuration: drawn ? '850ms, 850ms, 180ms, 180ms, 180ms' : '0ms',
                transitionTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)',
                transitionDelay: drawn ? `${index * 55}ms, ${index * 55}ms, 0ms, 0ms, 0ms` : '0ms',
              }}
            >
              <title>{`${row.label}: ${formatCurrency(row.chartValue)}`}</title>
            </circle>
          );
        })}
      </svg>

      <div className="pointer-events-none absolute inset-[27%] flex flex-col items-center justify-center rounded-full border border-gray-100 bg-white/95 px-3 text-center shadow-xl shadow-gray-900/5 backdrop-blur dark:border-gray-700 dark:bg-gray-900/95">
        <div className="max-w-full truncate text-[10px] font-semibold uppercase tracking-[0.15em] text-gray-400 dark:text-gray-500">
          {hoveredRow ? hoveredRow.label : metric.centerLabel}
        </div>
        <div className={`mt-1 text-[clamp(1rem,4vw,1.35rem)] font-bold leading-tight ${
          centerValue < 0
            ? 'text-rose-600 dark:text-rose-300'
            : 'text-gray-950 dark:text-white'
        }`}>
          {activeRows.length > 0 ? formatCompactCurrency(centerValue) : 'Пусто'}
        </div>
        <div className="mt-1 text-[10px] font-medium text-gray-400 dark:text-gray-500">
          {centerShare === null
            ? `${enabledCount} из ${totalCount} включено`
            : `${formatPercent(centerShare)} круга`}
        </div>
      </div>
    </div>
  );
};

const ProfitDonut = ({
  rows,
  metricKey,
  hoveredKey,
  onHover,
  onToggle,
  enabledCount,
  totalCount,
}) => {
  const signature = `${metricKey}:${rows
    .map((row) => `${row.key}:${row.chartValue.toFixed(2)}`)
    .join('|')}`;

  return (
    <AnimatedDonutCanvas
      key={signature}
      rows={rows}
      metricKey={metricKey}
      hoveredKey={hoveredKey}
      onHover={onHover}
      onToggle={onToggle}
      enabledCount={enabledCount}
      totalCount={totalCount}
    />
  );
};

const MetricCard = ({ icon: Icon, label, value, note, tone = 'emerald' }) => {
  const tones = {
    emerald: 'from-emerald-500 to-teal-600 shadow-emerald-500/20',
    sky: 'from-sky-500 to-blue-600 shadow-sky-500/20',
    rose: 'from-rose-500 to-orange-500 shadow-rose-500/20',
    violet: 'from-violet-500 to-indigo-600 shadow-violet-500/20',
  };
  return (
    <div className="rounded-[24px] border border-white/70 bg-white/90 p-4 shadow-sm backdrop-blur dark:border-gray-800 dark:bg-gray-900/90">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-[0.17em] text-gray-400 dark:text-gray-500">
            {label}
          </div>
          <div className="mt-2 text-[clamp(1.35rem,5vw,1.9rem)] font-bold leading-none text-gray-950 dark:text-white">
            {value}
          </div>
          <div className="mt-2 text-xs leading-relaxed text-gray-500 dark:text-gray-400">{note}</div>
        </div>
        <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br text-white shadow-lg ${tones[tone] || tones.emerald}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
};

const DimensionCard = ({ dimension, rows, metricKey, onRowClick }) => {
  const Icon = dimension.icon;
  const metric = CHART_METRICS.find((item) => item.key === metricKey) || CHART_METRICS[0];
  const [hiddenKeys, setHiddenKeys] = useState(() => new Set());
  const [hoveredKey, setHoveredKey] = useState(null);
  const [expandedLegend, setExpandedLegend] = useState(false);

  useEffect(() => {
    setHiddenKeys(new Set());
    setHoveredKey(null);
    setExpandedLegend(false);
  }, [rows, metricKey]);

  const sortedRows = useMemo(() => sortRowsByMetric(rows, metricKey), [rows, metricKey]);
  const rowsByKey = useMemo(() => new Map(
    sortedRows.map((row) => [String(row.key), row])
  ), [sortedRows]);
  const chartGroups = useMemo(
    () => buildChartGroups(sortedRows, metricKey),
    [sortedRows, metricKey]
  );

  const chartRows = useMemo(() => addChartColors(
    chartGroups.map((group) => {
      const memberKeys = group.memberKeys || [String(group.key)];
      const enabledMembers = memberKeys
        .filter((key) => !hiddenKeys.has(key))
        .map((key) => rowsByKey.get(key))
        .filter(Boolean);
      const enabledAggregate = aggregateRows(enabledMembers, group.label, group.key);
      return {
        ...enabledAggregate,
        key: group.key,
        label: group.label,
        memberKeys,
        enabledMembers: enabledMembers.length,
        totalMembers: memberKeys.length,
        isDisabled: enabledMembers.length === 0,
        isPartial: enabledMembers.length > 0 && enabledMembers.length < memberKeys.length,
        colorMetricValue: getMetricValue(group, metricKey),
        originalMetricValue: getMetricValue(group, metricKey),
      };
    }),
    metricKey
  ), [chartGroups, hiddenKeys, metricKey, rowsByKey]);

  const detailedRows = useMemo(() => addChartColors(
    sortedRows.map((row) => ({
      ...row,
      memberKeys: [String(row.key)],
      enabledMembers: hiddenKeys.has(String(row.key)) ? 0 : 1,
      totalMembers: 1,
      isDisabled: hiddenKeys.has(String(row.key)),
      isPartial: false,
      colorMetricValue: getMetricValue(row, metricKey),
      originalMetricValue: getMetricValue(row, metricKey),
    })),
    metricKey
  ), [hiddenKeys, metricKey, sortedRows]);

  const enabledCount = sortedRows.reduce(
    (count, row) => count + (hiddenKeys.has(String(row.key)) ? 0 : 1),
    0
  );

  const toggleRow = (row) => {
    haptic('selection');
    const targetKeys = row.memberKeys || [String(row.key)];
    setHiddenKeys((current) => {
      const next = new Set(current);
      const allHidden = targetKeys.every((key) => next.has(key));
      targetKeys.forEach((key) => {
        if (allHidden) next.delete(key);
        else next.add(key);
      });
      return next;
    });
    setHoveredKey(null);
  };

  const resetRows = () => {
    haptic('tap');
    setHiddenKeys(new Set());
    setHoveredKey(null);
  };

  const legendRows = expandedLegend ? detailedRows : chartRows;
  const resolveChartKey = (row) => {
    if (!expandedLegend) return row.key;
    const group = chartRows.find((item) => item.memberKeys.includes(String(row.key)));
    return group?.key || null;
  };

  return (
    <section className="group/card overflow-hidden rounded-[30px] border border-gray-200 bg-white shadow-sm transition-shadow duration-500 hover:shadow-xl hover:shadow-gray-900/5 dark:border-gray-800 dark:bg-gray-900">
      <div className="border-b border-gray-100 px-5 py-4 dark:border-gray-800">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-slate-800 to-slate-950 text-white shadow-lg shadow-slate-900/15 dark:from-slate-700 dark:to-slate-900">
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-bold text-gray-950 dark:text-white">{dimension.title}</div>
            <div className="mt-0.5 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
              {dimension.description}
            </div>
          </div>
          </div>
          <div className="shrink-0 rounded-full bg-gray-100 px-2.5 py-1 text-[10px] font-semibold text-gray-500 transition-colors dark:bg-gray-800 dark:text-gray-400">
            {enabledCount}/{sortedRows.length}
          </div>
        </div>
      </div>

      {sortedRows.length === 0 ? (
        <div className="flex min-h-[360px] flex-col items-center justify-center px-6 text-center">
          <PieChart className="h-9 w-9 text-gray-300 dark:text-gray-700" />
          <div className="mt-3 text-sm font-medium text-gray-600 dark:text-gray-300">
            Нет данных по метрике «{metric.label}»
          </div>
          <div className="mt-1 text-xs text-gray-400 dark:text-gray-500">
            Выберите другую метрику или измените период.
          </div>
        </div>
      ) : (
        <>
          <div className="grid gap-4 p-5 md:grid-cols-[minmax(270px,310px)_minmax(0,1fr)] md:items-center">
            <div>
              <ProfitDonut
                rows={chartRows}
                metricKey={metricKey}
                hoveredKey={hoveredKey}
                onHover={setHoveredKey}
                onToggle={toggleRow}
                enabledCount={enabledCount}
                totalCount={sortedRows.length}
              />
              <div className="mx-auto mt-1 flex max-w-[310px] items-center justify-center gap-2 text-center text-[10px] font-medium text-gray-400 dark:text-gray-500">
                <MousePointer2 className="h-3.5 w-3.5 shrink-0" />
                Нажмите на сектор или строку, чтобы исключить её
              </div>
            </div>

            <div className={`min-w-0 space-y-1 ${expandedLegend ? 'max-h-[360px] overflow-y-auto pr-1' : ''}`}>
              {legendRows.map((row) => {
                const clickable = (
                  typeof onRowClick === 'function'
                  && row.memberKeys.length === 1
                  && row.key !== '__other__'
                );
                const shownValue = row.isDisabled ? row.originalMetricValue : row.chartValue;
                const valueIsLoss = metricKey === 'profit' && shownValue < 0;
                const chartHoverKey = resolveChartKey(row);
                return (
                  <div
                    key={row.key}
                    className={`group/row flex items-stretch gap-1 rounded-2xl transition-all duration-300 ${
                      row.isDisabled
                        ? 'opacity-40'
                        : hoveredKey === chartHoverKey
                          ? 'bg-gray-100 shadow-sm dark:bg-gray-800'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-800/70'
                    }`}
                    onMouseEnter={() => setHoveredKey(chartHoverKey)}
                    onMouseLeave={() => setHoveredKey(null)}
                  >
                    <button
                      type="button"
                      onClick={() => toggleRow(row)}
                      aria-pressed={!row.isDisabled}
                      className="flex min-w-0 flex-1 items-center gap-2.5 rounded-2xl px-2.5 py-2.5 text-left"
                    >
                      <span
                        className="h-3 w-3 shrink-0 rounded-full shadow-sm ring-2 ring-white dark:ring-gray-900"
                        style={{ backgroundColor: row.color }}
                      />
                      <span className="min-w-0 flex-1">
                        <span className={`block truncate text-xs font-semibold text-gray-800 dark:text-gray-200 ${
                          row.isDisabled ? 'line-through' : ''
                        }`}>
                          {row.label}
                        </span>
                        <span className="mt-0.5 block truncate text-[10px] text-gray-400 dark:text-gray-500">
                          {row.totalMembers > 1
                            ? `${row.enabledMembers} из ${row.totalMembers} позиций`
                            : `${row.orders_count} зак. · маржа ${formatPercent(row.margin)}`}
                        </span>
                      </span>
                      <span className="shrink-0 text-right">
                        <span className={`block text-xs font-bold ${
                          row.isDisabled
                            ? 'text-gray-400'
                            : valueIsLoss
                              ? 'text-rose-600 dark:text-rose-300'
                              : metricKey === 'profit'
                                ? 'text-emerald-600 dark:text-emerald-300'
                                : 'text-gray-900 dark:text-white'
                        }`}>
                          {formatCompactCurrency(shownValue)}
                        </span>
                        <span className="mt-0.5 flex items-center justify-end gap-1 text-[10px] text-gray-400 dark:text-gray-500">
                          {row.isDisabled ? (
                            <>
                              <EyeOff className="h-3 w-3" />
                              выключено
                            </>
                          ) : (
                            <>
                              <Eye className="h-3 w-3" />
                              в диаграмме
                            </>
                          )}
                        </span>
                      </span>
                    </button>
                    {clickable ? (
                  <button
                    type="button"
                    onClick={() => onRowClick(row)}
                        className="my-1.5 mr-1.5 flex w-8 shrink-0 items-center justify-center rounded-xl text-gray-400 opacity-0 transition-all hover:bg-white hover:text-gray-900 group-hover/row:opacity-100 dark:hover:bg-gray-700 dark:hover:text-white"
                        aria-label={`Открыть ${row.label}`}
                  >
                        <ArrowUpRight className="h-4 w-4" />
                  </button>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 bg-gray-50/70 px-5 py-3 dark:border-gray-800 dark:bg-gray-950/30">
            <button
              type="button"
              onClick={() => setExpandedLegend((value) => !value)}
              className="inline-flex items-center gap-1.5 rounded-xl px-2.5 py-2 text-xs font-semibold text-gray-600 transition hover:bg-white hover:text-gray-950 hover:shadow-sm dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white"
            >
              {expandedLegend ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              {expandedLegend ? 'Только крупные сегменты' : `Все позиции (${sortedRows.length})`}
            </button>
            {hiddenKeys.size > 0 ? (
              <button
                type="button"
                onClick={resetRows}
                className="inline-flex items-center gap-1.5 rounded-xl bg-gray-900 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-gray-700 dark:bg-white dark:text-gray-950 dark:hover:bg-gray-200"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Включить все
              </button>
            ) : (
              <span className="text-[10px] font-medium uppercase tracking-[0.13em] text-gray-400 dark:text-gray-600">
                {metric.label}
              </span>
            )}
          </div>
        </>
      )}
    </section>
  );
};

const ProfitRadar = () => {
  const navigate = useNavigate();
  const initialRange = useMemo(currentMonthRange, []);
  const [dateFrom, setDateFrom] = useState(initialRange.from);
  const [dateTo, setDateTo] = useState(initialRange.to);
  const [activePreset, setActivePreset] = useState('month');
  const [chartMetric, setChartMetric] = useState('profit');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { showToast } = useToast();
  const [error, setError] = useState('');

  const loadRadar = useCallback(async (signal) => {
    if (!dateFrom || !dateTo) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
      const response = await fetch(`${API_URL}/api/profit-radar?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        signal,
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.detail || 'Не удалось загрузить радар прибыли');
      }
      setData(payload);
    } catch (loadError) {
      if (loadError.name !== 'AbortError') {
        const msg = loadError.message || 'Не удалось загрузить данные';
        setError(msg);
        showToast(msg, 'error');
      }
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    const controller = new AbortController();
    loadRadar(controller.signal);
    return () => controller.abort();
  }, [loadRadar]);

  const applyPreset = (preset) => {
    haptic('selection');
    const ranges = {
      month: currentMonthRange,
      '30': () => rollingRange(30),
      '90': () => rollingRange(90),
      year: currentYearRange,
    };
    const range = ranges[preset]();
    setActivePreset(preset);
    setDateFrom(range.from);
    setDateTo(range.to);
  };

  const summary = data?.summary || {};
  const quality = data?.quality || {};
  const dimensions = data?.dimensions || {};
  const resultIsLoss = Number(summary.profit || 0) < 0;
  const directShare = Math.max(0, Math.min(100, Number(quality.direct_expense_share || 0)));

  return (
    <div className="relative min-h-full overflow-hidden px-4 pb-24 pt-4">
      <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[420px] bg-[radial-gradient(circle_at_top_left,rgba(16,185,129,0.13),transparent_38%),radial-gradient(circle_at_top_right,rgba(99,102,241,0.12),transparent_42%)]" />

      <div className="space-y-4 desktop-content-container-wide">
        <section className="overflow-hidden rounded-[30px] border border-slate-800 bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950 p-5 text-white shadow-2xl shadow-slate-900/20">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-200">
                <CircleDollarSign className="h-3.5 w-3.5" />
                Только разработчики
              </div>
              <div className="mt-4 text-xl font-bold tracking-tight sm:text-2xl">Финансовый результат без иллюзий</div>
              <p className="mt-2 max-w-xl text-sm leading-relaxed text-slate-300">
                Фактическая выручка по заказам, прямые расходы и расчётное распределение общих затрат в одном срезе.
              </p>
            </div>
            <div className={`rounded-[24px] border px-4 py-3 ${
              resultIsLoss
                ? 'border-rose-400/30 bg-rose-400/10'
                : 'border-emerald-400/30 bg-emerald-400/10'
            }`}>
              <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400">Расчётная прибыль</div>
              <div className={`mt-1 text-2xl font-bold ${resultIsLoss ? 'text-rose-300' : 'text-emerald-300'}`}>
                {loading && !data ? '…' : formatCompactCurrency(summary.profit)}
              </div>
              <div className="mt-1 text-xs text-slate-400">Маржа {formatPercent(summary.margin)}</div>
            </div>
          </div>

          <div className="mt-5 grid gap-3 border-t border-white/10 pt-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
            <div>
              <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Быстрый период</div>
              <div className="flex flex-wrap gap-2">
                {[
                  ['month', 'Этот месяц'],
                  ['30', '30 дней'],
                  ['90', '90 дней'],
                  ['year', 'Этот год'],
                ].map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => applyPreset(key)}
                    className={`rounded-xl border px-3 py-2 text-xs font-medium transition-colors ${
                      activePreset === key
                        ? 'border-emerald-300/40 bg-emerald-400/20 text-emerald-100'
                        : 'border-white/10 bg-white/5 text-slate-300 hover:bg-white/10'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap items-end gap-2">
              <FloatingField label="С" className="min-w-[145px] flex-1 lg:flex-none">
                <input
                  type="date"
                  value={dateFrom}
                  max={dateTo}
                  onChange={(event) => {
                    setActivePreset('custom');
                    setDateFrom(event.target.value);
                  }}
                  className="h-10 w-full rounded-xl border border-white/10 bg-white/10 px-3 text-xs text-white outline-none transition focus:border-emerald-400/50"
                />
              </FloatingField>
              <FloatingField label="По" className="min-w-[145px] flex-1 lg:flex-none">
                <input
                  type="date"
                  value={dateTo}
                  min={dateFrom}
                  onChange={(event) => {
                    setActivePreset('custom');
                    setDateTo(event.target.value);
                  }}
                  className="h-10 w-full rounded-xl border border-white/10 bg-white/10 px-3 text-xs text-white outline-none transition focus:border-emerald-400/50"
                />
              </FloatingField>
              <button
                type="button"
                onClick={() => {
                  haptic('tap');
                  loadRadar();
                }}
                disabled={loading}
                className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/10 text-white transition hover:bg-white/15 disabled:opacity-50"
                aria-label="Обновить"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </section>

        {error ? (
          <div className="flex items-start gap-3 rounded-[24px] border border-rose-200 bg-rose-50 px-4 py-4 text-rose-800 dark:border-rose-900 dark:bg-rose-950/25 dark:text-rose-200">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-semibold">Не удалось построить радар</div>
              <div className="mt-1 text-xs">{error}</div>
            </div>
          </div>
        ) : null}

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <MetricCard
            icon={WalletCards}
            label="Выручка заказов"
            value={loading && !data ? '…' : formatCompactCurrency(summary.revenue)}
            note={`${summary.orders_count || 0} заказов в финансовом срезе`}
            tone="sky"
          />
          <MetricCard
            icon={ArrowDownRight}
            label="Все расходы"
            value={loading && !data ? '…' : formatCompactCurrency(summary.expenses)}
            note={`Прямые ${formatCompactCurrency(summary.direct_expenses)} · общие ${formatCompactCurrency(summary.shared_expenses)}`}
            tone="rose"
          />
          <MetricCard
            icon={resultIsLoss ? ArrowDownRight : ArrowUpRight}
            label="Расчётная прибыль"
            value={loading && !data ? '…' : formatCompactCurrency(summary.profit)}
            note={`До распределения общих расходов: ${formatCompactCurrency(summary.gross_profit)}`}
            tone={resultIsLoss ? 'rose' : 'emerald'}
          />
          <MetricCard
            icon={PieChart}
            label="Маржа"
            value={loading && !data ? '…' : formatPercent(summary.margin)}
            note={`${summary.transactions_count || 0} подтверждённых движений`}
            tone="violet"
          />
        </div>

        <section className="rounded-[28px] border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex min-w-0 items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
                <Info className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-bold text-gray-950 dark:text-white">Точность распределения расходов</div>
                <div className="mt-1 max-w-2xl text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                  {quality.method || 'Прямые расходы учитываются по заказу, общие распределяются пропорционально выручке.'}
                </div>
              </div>
            </div>
            <div className="shrink-0 text-right">
              <div className="text-2xl font-bold text-gray-950 dark:text-white">{formatPercent(directShare)}</div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-gray-400">расходов привязано прямо</div>
            </div>
          </div>
          <div className="mt-4 h-2.5 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
            <div
              className="h-full rounded-full bg-gradient-to-r from-amber-400 via-emerald-400 to-emerald-500 transition-all duration-700"
              style={{ width: `${directShare}%` }}
            />
          </div>
          <div className="mt-3 grid gap-2 text-xs sm:grid-cols-3">
            <div className="rounded-2xl bg-gray-50 px-3 py-2.5 text-gray-600 dark:bg-gray-950/50 dark:text-gray-300">
              Доходы без заказа: <span className="font-semibold">{formatCurrency(quality.unlinked_income)}</span>
            </div>
            <div className="rounded-2xl bg-gray-50 px-3 py-2.5 text-gray-600 dark:bg-gray-950/50 dark:text-gray-300">
              Нераспределённые расходы: <span className="font-semibold">{formatCurrency(quality.unallocated_expenses)}</span>
            </div>
            <div className="rounded-2xl bg-gray-50 px-3 py-2.5 text-gray-600 dark:bg-gray-950/50 dark:text-gray-300">
              Потерянные связи с заказами: <span className="font-semibold">{quality.orders_missing || 0}</span>
            </div>
          </div>
        </section>

        <section className="overflow-hidden rounded-[28px] border border-gray-200 bg-gradient-to-br from-white via-white to-emerald-50/60 p-4 shadow-sm dark:border-gray-800 dark:from-gray-900 dark:via-gray-900 dark:to-emerald-950/20">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-lg shadow-emerald-500/20">
                <Sparkles className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-bold text-gray-950 dark:text-white">Что сравниваем в кругах</div>
                <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                  Сектора интерактивные: их можно временно выключать и возвращать.
                </div>
              </div>
            </div>
            <div
              className="flex w-full rounded-2xl bg-gray-100 p-1 sm:w-auto dark:bg-gray-800"
              role="group"
              aria-label="Метрика круговых диаграмм"
            >
              {CHART_METRICS.map((metric) => (
                <button
                  key={metric.key}
                  type="button"
                  onClick={() => {
                    haptic('selection');
                    setChartMetric(metric.key);
                  }}
                  aria-pressed={chartMetric === metric.key}
                  className={`min-w-0 flex-1 rounded-xl px-3 py-2 text-xs font-semibold transition-all duration-300 sm:flex-none ${
                    chartMetric === metric.key
                      ? 'bg-white text-gray-950 shadow-sm dark:bg-gray-700 dark:text-white'
                      : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
                  }`}
                >
                  {metric.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {loading && !data ? (
          <div className="grid gap-4 xl:grid-cols-2">
            {[0, 1, 2, 3].map((item) => (
              <div key={item} className="h-[470px] animate-pulse rounded-[30px] border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900" />
            ))}
          </div>
        ) : (
          <div className="grid gap-4 xl:grid-cols-2">
            {DIMENSIONS.map((dimension) => (
              <DimensionCard
                key={dimension.key}
                dimension={dimension}
                rows={dimensions[dimension.key] || []}
                metricKey={chartMetric}
                onRowClick={dimension.key === 'orders'
                  ? (row) => navigate(`/order/${row.key}`)
                  : undefined}
              />
            ))}
          </div>
        )}

        {(data?.top_losses || []).length > 0 ? (
          <section className="rounded-[28px] border border-rose-200 bg-gradient-to-br from-white to-rose-50 p-4 shadow-sm dark:border-rose-950 dark:from-gray-900 dark:to-rose-950/20">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-rose-100 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div>
                <div className="text-sm font-bold text-gray-950 dark:text-white">Заказы с отрицательным результатом</div>
                <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Первые кандидаты для проверки расходов и ценообразования.</div>
              </div>
            </div>
            <div className="mt-4 grid gap-2 lg:grid-cols-2">
              {data.top_losses.map((row) => (
                <button
                  key={row.key}
                  type="button"
                  onClick={() => navigate(`/order/${row.key}`)}
                  className="flex items-center justify-between gap-3 rounded-2xl border border-rose-100 bg-white/90 px-3 py-3 text-left transition hover:border-rose-200 hover:shadow-sm dark:border-rose-950 dark:bg-gray-900/80"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-xs font-semibold text-gray-900 dark:text-white">{row.label}</span>
                    <span className="mt-0.5 block text-[10px] text-gray-500 dark:text-gray-400">
                      Выручка {formatCurrency(row.revenue)} · расходы {formatCurrency(row.expenses)}
                    </span>
                  </span>
                  <span className="shrink-0 text-sm font-bold text-rose-600 dark:text-rose-300">
                    {formatCurrency(row.profit)}
                  </span>
                </button>
              ))}
            </div>
          </section>
        ) : null}

        <div className="flex items-center justify-center gap-2 py-2 text-[10px] uppercase tracking-[0.15em] text-gray-400 dark:text-gray-600">
          <CalendarDays className="h-3.5 w-3.5" />
          {data?.period?.basis || 'Дата подтверждённой финансовой операции'}
        </div>
      </div>
    </div>
  );
};

export default ProfitRadar;
